
import { mapActions, mapGetters, mapState } from 'vuex';
import AdvancedInputsForm from '@/ui/retirement-calculator/advanced-inputs-form';
import DialogCentered from '@/components/molecules/dialog/dialog-centered';
import FButton from '@/components/atoms/buttons/h-button';
import FIcon from '@/components/atoms/icons/h-icon';
import ProgressBar from '@/components/atoms/progress-bar/progress-bar.vue';
import { useABTest } from '@/utils/useAbTest';

import HousingForm from '@/ui/retirement-calculator/housing-form';
import MainHeader from '@/ui/retirement-calculator/main-header';
import MobileFormDialog from '@/ui/retirement-calculator/mobile-form-dialog';
import OfferCta from '@/ui/retirement-calculator/offer-cta.vue';
import PensionSolutionsTable from '@/ui/retirement-calculator/results/pension-solutions-table.vue';
import PersonalForm from '@/ui/retirement-calculator/personal-form';
import PieChart from '@/ui/retirement-calculator/results/pie-chart';
import PlaygroundGraph from '@/ui/retirement-calculator/playground/playground-graph';
import PlaygroundSolutionCards from '@/ui/retirement-calculator/playground/playground-solution-cards.vue';
import PlaygroundSolutionsSliders from '@/ui/retirement-calculator/playground/contribution-sliders.vue';

export default {
  name: 'PensionCalculatorIO',

  components: {
    AdvancedInputsForm,
    DialogCentered,
    FButton,
    FIcon,
    HousingForm,
    MainHeader,
    MobileFormDialog,
    OfferCta,
    PensionSolutionsTable,
    PersonalForm,
    PieChart,
    PlaygroundGraph,
    PlaygroundSolutionCards,
    PlaygroundSolutionsSliders,
    ProgressBar,
  },

  layout: 'default-without-cta-breadcrumbs',

  data() {
    return {
      tab: null,
      isAdvancedInputsDialogShown: false,
      isMobileCalculatorDialogShown: false,
      hasDirtyGraphInputs: false,
      shouldShowSkeletons: false,
      showMobileEditCta: false,
      showProgressBar: false,
      retirementCalculatorAB: null,
    };
  },

  computed: {
    ...mapState({
      isLoading: (state) => state['pension-calculator'].isCalculating,

      retirementAge: (state) =>
        state['pension-calculator'].inputs.slider_desired_retirement_age,
      monthlyContribution: (state) =>
        state['pension-calculator'].inputs.slider_monthly_contribution,
      oneTimeContribution: (state) =>
        state['pension-calculator'].inputs.slider_one_time_contribution,
      retirementAgeDifference: (state) =>
        state['pension-calculator'].outputs.expected_retirement_age_difference,
    }),

    ...mapGetters({
      hasFormErrors: 'pension-calculator/hasFormErrors',
    }),

    shouldShowReCalculateButton() {
      return this.hasDirtyGraphInputs && !this.isMobileCalculatorDialogShown;
    },

    tabNames() {
      return Object.values(this.$t('tabs'));
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    isLocaleDE() {
      return this.$i18n.locale === 'de';
    },
  },

  watch: {
    retirementAge(newVal, prev) {
      this.setDirty(newVal, prev);
    },
    monthlyContribution(newVal, prev) {
      this.setDirty(newVal, prev);
    },
    oneTimeContribution(newVal, prev) {
      this.setDirty(newVal, prev);
    },
    isLoading(isLoading) {
      this.shouldShowSkeletons = isLoading;
    },
  },

  async mounted() {
    this.showMobileEditCta = true;
    await this.setupRetirementCalculatorAbTest();
  },

  methods: {
    ...mapActions({
      calculate: 'pension-calculator/calculate',
    }),

    setDirty(newVal, oldVal) {
      if (newVal !== oldVal) this.hasDirtyGraphInputs = true;
    },

    async handleAdvancedInputsClick() {
      this.isAdvancedInputsDialogShown = true;

      await this.$eventTrackClick(
        'Website Pension Calculator Advanced Inputs Link',
      );
    },

    trackScrollProgress(progress) {
      this.showProgressBar = progress > 0 && progress < 100;
    },

    scrollOnMobile() {
      if (this.isMobile) {
        this.$vuetify.goTo('h1', {
          easing: 'easeInOutCubic',
        });
      }
    },

    async submitForm() {
      this.shouldShowSkeletons = true;
      await this.submit();

      this.scrollOnMobile();
      this.shouldShowSkeletons = false;
    },

    handleEditButtonClick() {
      this.trackCtaClick('Edit button');
      this.isMobileCalculatorDialogShown = true;
      this.showMobileEditCta = false;
    },

    async handleReCalculateClick() {
      this.trackCtaClick('Recalculate');

      this.isAdvancedInputsDialogShown = false;
      await this.submit({
        includeContributionValues: true,
      });
    },

    async handleCalculatorButtonClick() {
      if (!this.tab) {
        this.tab = 1;
        this.trackCtaClick('Continue');
      } else {
        this.trackCtaClick('Calculate');
        await this.submit();
      }
    },

    async submit({ includeContributionValues = false } = {}) {
      this.isMobileCalculatorDialogShown = false;
      await this.calculate({ includeContributionValues });

      this.$emit('submit');
      this.hasDirtyGraphInputs = false;
    },

    async trackCtaClick(type) {
      await this.$eventTrackClick(
        'Website Pension Calculator Calculate Button',
        {
          Type: type,
        },
      );
    },

    setupRetirementCalculatorAbTest() {
      const experimentValue = useABTest(
        'hf-retirement-calculator-revamp',
        'control',
      );
      const variant = experimentValue === 'new' ? 'new' : 'control';

      if (experimentValue !== 'control') {
        this.retirementCalculatorAB = experimentValue;
      }

      this.$setUserProperties({ 'hf-retirement-calculator-revamp': variant });
    },
  },
};
